import { useAuth } from "@clerk/clerk-react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { green, grey } from "@mui/material/colors";
import { Editor as TipTapEditor, useEditor } from "@tiptap/react";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import {
  BentoContact,
  BentoContactSource,
  OutreachDraft,
  OutreachEmailDraft,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import { LinkModal } from "components/Editor/LinkModal";
import MenuOptions from "components/Editor/Menu";
import MergeTagChip from "components/Editor/MergeTagWarning/MergeTagChip";
import { VariableModal } from "components/Editor/Variable/VariableModal";
import Editor from "components/Editor/quicksend";
import { extensions } from "components/Editor/utils";
import TemplateEditButton from "components/TemplateEditButton";
import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import AttachmentManager from "features/Influencer/ContactList/AttachmentManager";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import ContactScore from "features/Influencer/QuickSendIndividualDrawer/PreviewPerContact/PreviewIndividual/ContactScore";
import FollowUpTab from "features/Influencer/QuickSendIndividualDrawer/PreviewPerContact/PreviewIndividual/FollowUpTab";
import { fetcherAuth } from "utils/api";
import { contactReplied } from "utils/influencer";
import {
  containsMergeTags,
  formatNumber,
  getVariablesFromHtml,
} from "utils/string";
import { Url, useLinkEditor } from "utils/useLinkEditor";
import { useVariableEditor } from "utils/useVariablesEditor";

import { AutomationBrandEmail, OutreachAutomationBrand } from "../../types";

const DEFAULT_FOLLOWUP_DELAY_DAYS = 4;

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 2,
  borderBottom: `1px solid ${grey[300]}`,
  minHeight: 40,
};

const labelStyle = {
  fontSize: "12px",
  color: grey[500],
  flexShrink: 0,
  width: 70,
};

const chipBase = {
  textTransform: "none",
  py: 2,
  borderRadius: 0,
  borderTopLeftRadius: 4,
  borderTopRightRadius: 4,
  color: "primary.main",
  border: `1px solid ${grey[300]}`,
  "&:hover": { background: "none" },
};

const activeChip = {
  ...chipBase,
  background: "white",
  borderBottom: "2px white solid",
};

const inactiveChip = { ...chipBase };

const noBorder = {
  boxShadow: "none",
  ".MuiOutlinedInput-notchedOutline": { border: 0 },
  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    border: 0,
  },
  "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: 0,
  },
};

const templateSelectStyle = {
  ml: -2,
  "& .MuiSelect-select": { display: "flex", alignItems: "center" },
  ...noBorder,
  color: "primary.main",
};

const templateMenuNameStyle = (isAutogenerated?: boolean) => ({
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  maxWidth: { xs: 240, md: 500 },
  color: isAutogenerated ? "info.main" : "primary.main",
});

interface BrandPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  brand: OutreachAutomationBrand;
}

export default function BrandPreviewDialog({
  open,
  onClose,
  brand,
}: BrandPreviewDialogProps) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { outreachTemplates, followupTemplates, setCreateTemplateDialogOpen } =
    useContext(OutreachTemplatesContext);
  const isSubscribedToPlan =
    currentOrg?.isProUser ||
    currentOrg?.isAutopilotUser ||
    currentOrg?.isVaUser;

  const isDraftMode = !!brand.outreachDraftId;

  const getContactDisplayName = (contact: BentoContact | null) => {
    if (!contact) return "";
    const firstName = contact.name?.includes("Team")
      ? contact.name
      : contact.name?.split(" ")[0];
    let titleAndName = "";
    if (firstName && contact.title) {
      titleAndName = `${firstName}, ${contact.title}`;
    } else if (firstName) {
      titleAndName = firstName;
    } else if (contact.title) {
      titleAndName = contact.title;
    }
    return `${titleAndName} (${contact.email})`;
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [confirmContactOpen, setConfirmContactOpen] = useState(false);
  const [pendingContactId, setPendingContactId] = useState<number | null>(null);

  const handleClose = () => {
    if (hasChanges) {
      setConfirmCloseOpen(true);
    } else {
      onClose();
    }
  };

  // Draft mode state
  const [draftData, setDraftData] = useState<OutreachDraft | null>(null);
  const [emailDrafts, setEmailDrafts] = useState<OutreachEmailDraft[]>([]);
  // Stable "original" entries built once on load — always shown in dropdown
  // In draft mode: "Draft - X", in email mode: "Current - X"
  const originalEntries = useRef<(Template | null)[]>([]);
  // Tracks whether user has explicitly changed template per tab
  const userChangedTemplate = useRef<boolean[]>([]);

  // Email mode state
  const [emails, setEmails] = useState<AutomationBrandEmail[]>([]);

  // Contact state
  const [contacts, setContacts] = useState<BentoContact[]>([]);
  const [selectedContact, setSelectedContact] = useState<BentoContact | null>(
    null,
  );

  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setHasChanges(false);
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchData = async () => {
    if (!currentOrg?.id) return;
    setLoading(true);

    try {
      // Fetch contacts
      const contactsRes = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/bento-brands/${brand.bentoBrandId}/contacts`,
        "GET",
      );
      const fetchedContacts: BentoContact[] =
        contactsRes.selectableContacts || [];
      setContacts(fetchedContacts);

      if (isDraftMode) {
        // Draft mode: fetch the outreach draft
        const draftRes = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/bento-brands/${brand.bentoBrandId}/outreach-draft`,
          "GET",
        );
        const draft: OutreachDraft | null = draftRes.outreachDraft;
        setDraftData(draft);
        // Normalize snake_case JSONB keys to camelCase
        // (OutreachEmailDraft is a BaseModel, not CamelCaseModel, so
        // JSONB values keep snake_case keys from .dict())
        const normalizedDrafts = (draft?.emailDrafts || []).map((d: any) => ({
          emailTemplateId: d.emailTemplateId ?? d.email_template_id,
          emailTemplateName: d.emailTemplateName ?? d.email_template_name,
          subject: d.subject,
          body: d.body,
          attachment: d.attachment,
          contentType: d.contentType ?? d.content_type,
          delayDays: d.delayDays ?? d.delay_days,
        }));
        setEmailDrafts(normalizedDrafts);
        userChangedTemplate.current = normalizedDrafts.map(() => false);

        // Set initial selected contact from draft
        if (draft?.bentoContactId && fetchedContacts.length > 0) {
          const match = fetchedContacts.find(
            (c) => c.id === draft.bentoContactId,
          );
          if (match) setSelectedContact(match);
        }
      } else {
        // Email mode: fetch emails for the automation brand
        const emailsRes = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/outreach-automations/brands/${brand.id}/emails`,
          "GET",
        );
        const fetchedEmails: AutomationBrandEmail[] = emailsRes.emails || [];
        setEmails(fetchedEmails);
        userChangedTemplate.current = fetchedEmails.map(() => false);

        // Set initial selected contact from first email's outreach contact
        if (fetchedEmails.length > 0 && fetchedEmails[0].outreachContact) {
          const oc = fetchedEmails[0].outreachContact;
          if (oc.bentoContactId && fetchedContacts.length > 0) {
            const match = fetchedContacts.find(
              (c) => c.id === oc.bentoContactId,
            );
            if (match) setSelectedContact(match);
          }
        }
      }
    } catch (e) {
      setErrorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const handleContactChange = async (contactId: number) => {
    const contact = contacts.find((c) => c.id === contactId);
    if (!contact || !currentOrg?.id) return;
    setSelectedContact(contact);
    setHasChanges(true);

    if (!isDraftMode) {
      // Email mode: call backend to re-personalize
      try {
        setSaving(true);
        const res = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/outreach-automations/brands/${brand.id}/contact`,
          "PUT",
          {},
          { bentoContactId: contactId },
        );
        setEmails(res.emails || []);
        setHasChanges(false);
      } catch (e) {
        setErrorAlert(e);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleSubjectChange = (value: string) => {
    setHasChanges(true);
    if (isDraftMode) {
      setEmailDrafts((prev) =>
        prev.map((d, i) => (i === 0 ? { ...d, subject: value } : d)),
      );
    } else {
      setEmails((prev) =>
        prev.map((e, i) => (i === 0 ? { ...e, subject: value } : e)),
      );
    }
  };

  const handleSave = async () => {
    if (!currentOrg?.id) return;
    setSaving(true);

    try {
      if (isDraftMode) {
        // Save via outreach-draft POST endpoint
        await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/bento-brands/${brand.bentoBrandId}/outreach-draft`,
          "POST",
          {},
          {
            emailDrafts: emailDrafts.map((d) => ({
              email_template_id: d.emailTemplateId,
              email_template_name: d.emailTemplateName,
              subject: d.subject,
              body: d.body,
              attachment: d.attachment,
              content_type: d.contentType,
              delay_days: d.delayDays,
            })),
            previewContact: selectedContact
              ? {
                  id: selectedContact.id,
                  name: selectedContact.name,
                  email: selectedContact.email,
                  title: selectedContact.title,
                }
              : undefined,
          },
        );
      } else {
        // Save via PUT emails endpoint
        await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/outreach-automations/brands/${brand.id}/emails`,
          "PUT",
          {},
          {
            emails: emails.map((e) => ({
              emailId: e.id,
              subject: e.subject,
              body: e.body,
              attachment: e.attachment ?? "",
              templateId: e.templateId,
            })),
          },
        );
      }
      setHasChanges(false);
      // Reset original entries so they rebuild from the now-saved state
      originalEntries.current = [];
      userChangedTemplate.current = dataItems.map(() => false);
      setAlert("Changes saved successfully", "success");
      onClose();
    } catch (e) {
      setErrorAlert(e);
    } finally {
      setSaving(false);
    }
  };

  const dataItems = isDraftMode ? emailDrafts : emails;
  const followups = dataItems.slice(1);

  const handleAddFollowUp = () => {
    if (followups.length >= MAXIMUM_FOLLOWUP_ALLOWED) return;

    setHasChanges(true);
    const newIndex = dataItems.length;
    if (followupTemplates?.length > 0) {
      const template = followupTemplates[0];
      setEmailDrafts((prev) => [
        ...prev,
        {
          emailTemplateId: template.id,
          emailTemplateName: template.name,
          subject: template.subject ?? "",
          body: template.body ?? "",
          attachment: template.attachment,
          contentType: TemplateContentType.FOLLOWUP,
          delayDays: DEFAULT_FOLLOWUP_DELAY_DAYS,
        },
      ]);
    } else {
      setEmailDrafts((prev) => [
        ...prev,
        {
          body: "",
          contentType: TemplateContentType.FOLLOWUP,
          delayDays: DEFAULT_FOLLOWUP_DELAY_DAYS,
        },
      ]);
    }
    setActiveTab(newIndex);
  };

  const handleDelayChange = (index: number, value: number) => {
    setHasChanges(true);
    setEmailDrafts((prev) =>
      prev.map((d, i) => (i === index ? { ...d, delayDays: value } : d)),
    );
  };

  const handleRemoveFollowUp = (index: number) => {
    setHasChanges(true);
    setEmailDrafts((prev) => {
      const copy = [...prev];
      copy.splice(index, 1);
      return copy;
    });
    if (activeTab >= index) {
      setActiveTab((prev) => Math.max(0, prev - 1));
    }
  };

  const currentSubject = isDraftMode
    ? emailDrafts[0]?.subject || ""
    : emails[0]?.subject || "";

  const currentBody = isDraftMode
    ? emailDrafts[activeTab]?.body || ""
    : emails[activeTab]?.body || "";

  // Template selection

  const currentContentType = isDraftMode
    ? emailDrafts[activeTab]?.contentType
    : emails[activeTab]?.contentType;
  const isOutreach = currentContentType === "outreach";
  // Filter out QuickSend draft entries that may linger in the shared context
  const savedTemplates = (
    isOutreach ? outreachTemplates : followupTemplates
  ).filter((t) => t.id && !isNaN(t.id));

  const currentTemplateId = isDraftMode
    ? emailDrafts[activeTab]?.emailTemplateId
    : emails[activeTab]?.templateId;

  // Build original entry once per tab (stable ref so it doesn't disappear)
  // Draft mode: "Draft - X", Email mode: "Current - X"
  if (!originalEntries.current[activeTab] && currentTemplateId) {
    if (isDraftMode && emailDrafts[activeTab]?.emailTemplateId) {
      const draft = emailDrafts[activeTab];
      const originalTemplate = savedTemplates.find(
        (t) => t.id === draft.emailTemplateId,
      );
      const entryName = originalTemplate
        ? `Draft - ${originalTemplate.name}`
        : `Draft - ${draft.emailTemplateName || "Template"}`;

      originalEntries.current[activeTab] = {
        emailTemplateId: draft.emailTemplateId,
        emailTemplateName: draft.emailTemplateName,
        name: entryName,
        subject: draft.subject || "",
        contentType:
          (draft.contentType as TemplateContentType) ||
          TemplateContentType.OUTREACH,
        body: draft.body,
        attachment: draft.attachment,
        isAutogenerated:
          draft.emailTemplateName === "Generated by Bento" || undefined,
      };
    } else if (!isDraftMode && emails[activeTab]?.templateId) {
      const email = emails[activeTab];
      const originalTemplate = savedTemplates.find(
        (t) => t.id === email.templateId,
      );
      const entryName = originalTemplate
        ? `Current - ${originalTemplate.name}`
        : `Current Email`;

      originalEntries.current[activeTab] = {
        emailTemplateId: email.templateId ?? undefined,
        name: entryName,
        subject: email.subject || "",
        contentType:
          (email.contentType as TemplateContentType) ||
          TemplateContentType.OUTREACH,
        body: email.body,
        attachment: email.attachment ?? undefined,
      };
    }
  }

  const originalEntry = originalEntries.current[activeTab] ?? null;

  // Always include the original entry in the dropdown if it exists
  const templateOptions = (() => {
    if (!originalEntry) return savedTemplates;
    const alreadyPresent = savedTemplates.some(
      (t) => !t.id && t.emailTemplateId === originalEntry.emailTemplateId,
    );
    if (alreadyPresent) return savedTemplates;
    return [...savedTemplates, originalEntry];
  })();

  const getTemplateValue = (t: Template) => {
    if (t.emailTemplateId) return `${t.name}-${t.emailTemplateId}`;
    if (t.id) return `${t.name}-${t.id}`;
    return t.name;
  };

  const getSelectValue = () => {
    // Before the user changes templates, select the original entry
    if (
      !userChangedTemplate.current[activeTab] &&
      originalEntry &&
      currentTemplateId === originalEntry.emailTemplateId
    ) {
      return getTemplateValue(originalEntry);
    }
    // After user picks a template, match by saved template id
    const match = templateOptions.find(
      (t) =>
        t.id === currentTemplateId || t.emailTemplateId === currentTemplateId,
    );
    return match ? getTemplateValue(match) : "";
  };

  const isAIGenerated = (template: Template) =>
    template?.isAutogenerated &&
    template?.emailTemplateName === "Generated by Bento";

  const getTemplateName = (template: Template) =>
    isAIGenerated(template)
      ? "Personalized For Brand"
      : template?.name || template.subject;

  const applyTemplate = async (value: string) => {
    let template: Template | null = null;
    for (const t of templateOptions) {
      if (getTemplateValue(t) === value) {
        template = t;
        break;
      }
    }
    if (!template || !currentOrg?.id) return;

    setSaving(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach/preview-quick-send`,
        "POST",
        {},
        {
          bentoBrandId: brand.bentoBrandId,
          emailTemplates: [
            {
              id: template.id || template.emailTemplateId,
              name: template.name,
              subject: template.subject,
              body: template.body || "",
              attachment: template.attachment,
              contentType: template.contentType,
            },
          ],
          previewContact: selectedContact
            ? {
                id: selectedContact.id,
                name: selectedContact.name,
                email: selectedContact.email,
                title: selectedContact.title,
              }
            : undefined,
        },
      );

      const personalized = res.preview?.outreachMessages?.[0];
      if (!personalized) return;

      setHasChanges(true);
      if (isDraftMode) {
        setEmailDrafts((prev) =>
          prev.map((d, i) =>
            i === activeTab
              ? {
                  ...d,
                  emailTemplateId:
                    personalized.templateId ??
                    template!.id ??
                    template!.emailTemplateId,
                  emailTemplateName:
                    personalized.templateName ?? template!.name,
                  subject: personalized.subject ?? d.subject,
                  body: personalized.body ?? d.body,
                  attachment: personalized.attachment ?? d.attachment,
                }
              : d,
          ),
        );
      } else {
        setEmails((prev) =>
          prev.map((e, i) =>
            i === activeTab
              ? {
                  ...e,
                  templateId: personalized.templateId ?? e.templateId,
                  subject: personalized.subject ?? e.subject,
                  body: personalized.body ?? e.body,
                  attachment: personalized.attachment ?? e.attachment,
                }
              : e,
          ),
        );
      }

      // Sync editor with new personalized body
      if (editor && !editor.isDestroyed && personalized.body) {
        editor.commands.setContent(personalized.body);
      }
    } catch (e) {
      setErrorAlert(e);
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateChange = (e: CustomEvent) => {
    const value = e.target.value as string;
    if (value === "add-template") return;
    // If user re-selects the original entry, treat it as going back to original
    const isOriginalSelection =
      originalEntry && value === getTemplateValue(originalEntry);
    userChangedTemplate.current[activeTab] = !isOriginalSelection;
    if (isOriginalSelection) {
      // Restore original content directly instead of calling API
      setHasChanges(true);
      if (isDraftMode) {
        setEmailDrafts((prev) =>
          prev.map((d, i) =>
            i === activeTab
              ? {
                  ...d,
                  emailTemplateId: originalEntry.emailTemplateId,
                  emailTemplateName: originalEntry.emailTemplateName,
                  subject: originalEntry.subject ?? d.subject,
                  body: originalEntry.body ?? d.body,
                  attachment: originalEntry.attachment ?? d.attachment,
                }
              : d,
          ),
        );
      } else {
        setEmails((prev) =>
          prev.map((e, i) =>
            i === activeTab
              ? {
                  ...e,
                  templateId: originalEntry.emailTemplateId ?? e.templateId,
                  subject: originalEntry.subject ?? e.subject,
                  body: originalEntry.body ?? e.body,
                  attachment: originalEntry.attachment ?? e.attachment,
                }
              : e,
          ),
        );
      }
      if (editor && !editor.isDestroyed && originalEntry.body) {
        editor.commands.setContent(originalEntry.body);
      }
      return;
    }
    applyTemplate(value);
  };

  const editor = useEditor(
    {
      extensions: extensions(),
      content: currentBody,
      onUpdate({ editor }) {
        setHasChanges(true);
        const html = editor.getHTML();
        if (isDraftMode) {
          setEmailDrafts((prev) =>
            prev.map((d, i) => (i === activeTab ? { ...d, body: html } : d)),
          );
        } else {
          setEmails((prev) =>
            prev.map((e, i) => (i === activeTab ? { ...e, body: html } : e)),
          );
        }
      },
    },
    [
      activeTab,
      isDraftMode ? draftData?.id : emails.map((e) => e.id).join(","),
    ],
  ) as TipTapEditor;

  const {
    modalIsOpen,
    url,
    setUrl,
    openModal,
    closeModal,
    saveLink,
    removeLink,
  } = useLinkEditor({ editor });

  const {
    variableModalIsOpen,
    variable,
    openVariableFilledModal,
    deleteVariableFilled,
    handleSaveVariable,
    closeVariableModal,
  } = useVariableEditor({ editor });

  const [showToolbar, setShowToolbar] = useState(false);
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [selectImageOpen, setSelectImageOpen] = useState(false);

  const currentAttachment = isDraftMode
    ? emailDrafts[activeTab]?.attachment || undefined
    : emails[activeTab]?.attachment || undefined;

  const handleAttachmentSelect = (attachmentUrl: string) => {
    setHasChanges(true);
    if (isDraftMode) {
      setEmailDrafts((prev) =>
        prev.map((d, i) =>
          i === activeTab ? { ...d, attachment: attachmentUrl } : d,
        ),
      );
    } else {
      setEmails((prev) =>
        prev.map((e, i) =>
          i === activeTab ? { ...e, attachment: attachmentUrl } : e,
        ),
      );
    }
  };

  const handleRemoveAttachment = () => {
    setHasChanges(true);
    if (isDraftMode) {
      setEmailDrafts((prev) =>
        prev.map((d, i) =>
          i === activeTab ? { ...d, attachment: undefined } : d,
        ),
      );
    } else {
      setEmails((prev) =>
        prev.map((e, i) => (i === activeTab ? { ...e, attachment: null } : e)),
      );
    }
  };

  const selectedImage = (imageUrl: string) => {
    if (editor) {
      editor.chain().focus().setImage({ src: imageUrl, size: "large" }).run();
    }
  };

  // Sync editor content when switching tabs or data reloads
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      editor.commands.setContent(currentBody);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, editor]);

  // Merge tag validation
  const editorHtml = editor?.getHTML() || "";
  const errorVariables = getVariablesFromHtml(editorHtml);
  const hasMergeTagInSubject = containsMergeTags(currentSubject);
  const hasMergeTagInBody = containsMergeTags(editorHtml);
  const hasTemplatingIssues =
    errorVariables.length > 0 || hasMergeTagInSubject || hasMergeTagInBody;

  const { bentoBrand } = brand;
  const displayName = bentoBrand.normalizedName || "Unknown Brand";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobileScreen}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          height: isMobileScreen ? undefined : "70vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          pb: 1,
          position: "relative",
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, flex: 1, minWidth: 0 }}>
          <Avatar
            src={bentoBrand.logoUrl}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: bentoBrand.backgroundLogoColor || "#e5e7eb",
              fontSize: 14,
              fontWeight: 600,
              color: "#014339",
              flexShrink: 0,
            }}
          >
            {displayName[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontWeight: 700, fontSize: 15, color: "#014339" }}
            >
              {displayName}
            </Typography>
            {/* Instagram + followers */}
            {bentoBrand.instagramUsername && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mt: 0.25,
                }}
              >
                <Typography sx={{ fontSize: 12, color: "rgba(1,67,57,0.5)" }}>
                  @{bentoBrand.instagramUsername}
                </Typography>
                {bentoBrand.instagramFollowerCount != null && (
                  <>
                    <Typography
                      sx={{ fontSize: 12, color: "rgba(1,67,57,0.5)" }}
                    >
                      &bull;
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, color: "rgba(1,67,57,0.5)" }}
                    >
                      {formatNumber(bentoBrand.instagramFollowerCount)}{" "}
                      followers
                    </Typography>
                  </>
                )}
              </Box>
            )}
            {/* Links + automation source */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 0.5,
              }}
            >
              {bentoBrand.website && (
                <Link
                  href={`https://${bentoBrand.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: 11,
                    color: "#014339",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <Box
                    component="span"
                    className="fa-solid fa-globe"
                    sx={{ fontSize: 10, mr: 0.5 }}
                  />
                  Website
                </Link>
              )}
              {bentoBrand.instagramUsername && (
                <Link
                  href={`https://instagram.com/${bentoBrand.instagramUsername}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: 11,
                    color: "#014339",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <Box
                    component="span"
                    className="fa-brands fa-instagram"
                    sx={{ fontSize: 10, mr: 0.5 }}
                  />
                  Instagram
                </Link>
              )}
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 500,
                  color: "#b1b3f8",
                }}
              >
                from: {brand.outreachAutomation.name}
              </Typography>
            </Box>
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <Box
            component="span"
            className="fa-solid fa-xmark"
            sx={{ fontSize: 14, color: "#014339" }}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 6,
            }}
          >
            <CircularProgress sx={{ color: "primary.main" }} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            {/* Scheduled: row (email mode only) */}
            {!isDraftMode && emails[0]?.sendAt && (
              <Box sx={rowStyle}>
                <Typography sx={labelStyle}>Scheduled:</Typography>
                <Typography sx={{ fontSize: 13, color: "#014339" }}>
                  {moment
                    .unix(Number(emails[0].sendAt))
                    .format("ddd, MMM D, YYYY [at] h:mm A")}
                  {brand.outreachAutomation.timezone &&
                    ` (${brand.outreachAutomation.timezone})`}
                </Typography>
              </Box>
            )}

            {/* To: row */}
            {contacts.length > 0 && (
              <Box sx={rowStyle}>
                <Typography sx={labelStyle}>To:</Typography>
                <Select
                  size="small"
                  variant="standard"
                  disableUnderline
                  value={selectedContact?.id || ""}
                  onChange={(e) => {
                    const newId = e.target.value as number;
                    if (newId === selectedContact?.id) return;
                    setPendingContactId(newId);
                    setConfirmContactOpen(true);
                  }}
                  disabled={saving}
                  sx={{ fontSize: 14, flex: 1 }}
                  renderValue={() => (
                    <Typography component="span" sx={{ fontSize: 14 }}>
                      {getContactDisplayName(selectedContact)}
                    </Typography>
                  )}
                >
                  {contacts.map((c) => (
                    <MenuItem
                      key={c.id}
                      value={c.id}
                      sx={{ whiteSpace: "normal" }}
                    >
                      {getContactDisplayName(c)}
                      {c.sourcedFromUserInbox &&
                        isSubscribedToPlan &&
                        " \u{1F48E}"}
                      {c.source === BentoContactSource.gmail_import && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: 12,
                            mx: 1,
                            color: "success.main",
                          }}
                          className="fa-solid fa-star"
                        />
                      )}
                      {isSubscribedToPlan && (
                        <>
                          {contactReplied(c) ? (
                            <Box
                              component="i"
                              sx={{ color: green[500], ml: 1 }}
                              className="fa-regular fa-check-double"
                            />
                          ) : c.lastSentToAt ? (
                            <Box
                              component="i"
                              sx={{ color: grey[400], ml: 1 }}
                              className="fa-regular fa-check-double"
                            />
                          ) : null}
                        </>
                      )}
                    </MenuItem>
                  ))}
                </Select>
                <ContactScore
                  bentoContact={selectedContact}
                  bentoBrand={brand.bentoBrand}
                />
              </Box>
            )}

            {/* Editable content area — dims when saving/loading */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                position: "relative",
              }}
            >
              {saving && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "rgba(255,255,255,0.5)",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={24} sx={{ color: "primary.main" }} />
                </Box>
              )}

              {/* Subject: row */}
              <Box sx={rowStyle}>
                <Typography sx={labelStyle}>Subject:</Typography>
                <TextField
                  fullWidth
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  value={currentSubject}
                  onChange={(e) => handleSubjectChange(e.target.value)}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-input": { fontSize: 14 },
                  }}
                />
              </Box>

              {/* Tab chips */}
              <Box sx={{ display: "flex", alignItems: "end", px: 2, pt: 1 }}>
                <Chip
                  label="Outreach"
                  size="small"
                  onClick={() => setActiveTab(0)}
                  sx={activeTab === 0 ? activeChip : inactiveChip}
                />
                {followups.map((followup, index) => {
                  const tabIndex = index + 1;
                  const isActive = activeTab === tabIndex;
                  if (isDraftMode) {
                    const days =
                      (followup as OutreachEmailDraft).delayDays ??
                      DEFAULT_FOLLOWUP_DELAY_DAYS;
                    return (
                      <FollowUpTab
                        key={index}
                        tabIndex={tabIndex}
                        days={days}
                        sx={isActive ? activeChip : inactiveChip}
                        onSelect={() => setActiveTab(tabIndex)}
                        onDelayChange={(val) =>
                          handleDelayChange(tabIndex, val)
                        }
                        onRemove={() => handleRemoveFollowUp(tabIndex)}
                      />
                    );
                  }
                  return (
                    <Chip
                      key={index}
                      label={`Follow-Up ${tabIndex}`}
                      size="small"
                      onClick={() => setActiveTab(tabIndex)}
                      sx={isActive ? activeChip : inactiveChip}
                    />
                  );
                })}
                {isDraftMode && followups.length < MAXIMUM_FOLLOWUP_ALLOWED && (
                  <Button
                    size="small"
                    sx={{ textTransform: "none" }}
                    onClick={handleAddFollowUp}
                  >
                    {followups.length > 0 ? "Add +" : "Add Follow-Up +"}
                  </Button>
                )}
              </Box>
              <Divider sx={{ mt: -0.1 }} />

              {/* Template selector */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  minHeight: 40,
                }}
              >
                <Typography sx={labelStyle}>Template:</Typography>
                {templateOptions.length > 0 || savedTemplates.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Select
                      onChange={handleTemplateChange}
                      value={getSelectValue()}
                      displayEmpty
                      size="small"
                      sx={templateSelectStyle}
                      renderValue={(val) => {
                        if (val) {
                          const match = templateOptions.find(
                            (t) => getTemplateValue(t) === val,
                          );
                          if (match) return getTemplateName(match);
                        }
                        return "Select template";
                      }}
                    >
                      <MenuItem
                        value="add-template"
                        onClick={() => setCreateTemplateDialogOpen(true)}
                        sx={{ color: "secondary.main" }}
                      >
                        <em>Add Template +</em>
                      </MenuItem>
                      {templateOptions.map((template, idx) => (
                        <MenuItem
                          key={idx}
                          value={getTemplateValue(template)}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          {isAIGenerated(template) && (
                            <Box
                              sx={{ color: "info.main", mr: 1 }}
                              component="i"
                              className="fa-solid fa-wand-magic-sparkles"
                            />
                          )}
                          <Box
                            sx={templateMenuNameStyle(isAIGenerated(template))}
                          >
                            {getTemplateName(template)}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {currentTemplateId &&
                      (userChangedTemplate.current[activeTab] ||
                        !originalEntry) && (
                        <TemplateEditButton
                          sx={{ ml: -1 }}
                          templateId={currentTemplateId}
                        />
                      )}
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setCreateTemplateDialogOpen(true)}
                    sx={{ height: 25, textTransform: "none" }}
                    disableElevation
                  >
                    Generate Template
                  </Button>
                )}
              </Box>

              {/* Scrollable editor content area */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  minHeight: 0,
                  cursor: "text",
                }}
              >
                {editor && <Editor editor={editor} />}

                {hasTemplatingIssues && (
                  <Typography
                    variant="body2"
                    sx={{ px: 2, pb: 0, mb: 1, color: "error.light" }}
                  >
                    {hasMergeTagInSubject || hasMergeTagInBody ? (
                      <>
                        Make sure you also fill out{" "}
                        <MergeTagChip mergeTag="{Brand Name}" /> and{" "}
                        <MergeTagChip mergeTag="{First Name}" /> in the email
                        and subject line.
                      </>
                    ) : (
                      <>
                        We noticed some templating characters (for example{" "}
                        <MergeTagChip mergeTag={errorVariables[0]} />) remaining
                        in your message. Please edit these before saving.
                      </>
                    )}
                  </Typography>
                )}

                {currentAttachment && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ml: 2,
                      mr: 8,
                      mb: 3,
                      color: "text.secondary",
                    }}
                  >
                    <Box className="fa-regular fa-paperclip" sx={{ mr: 1 }} />
                    <Typography
                      color="text.secondary"
                      sx={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        fontSize: 13,
                      }}
                    >
                      {currentAttachment.split("/").pop()}
                    </Typography>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => setAttachmentDialogOpen(true)}
                    >
                      <Box component="i" className="fa-solid fa-edit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="secondary"
                      onClick={handleRemoveAttachment}
                    >
                      <Box component="i" className="fa-solid fa-trash" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Sticky bottom toolbar */}
            {editor && (
              <Box sx={{ flexShrink: 0, px: 1 }}>
                {showToolbar && (
                  <Box
                    sx={{ boxShadow: 2, px: 1, background: "white" }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <MenuOptions
                      hideLink
                      editor={editor}
                      openModal={openModal}
                      isBubble
                      openVariableFilledModal={openVariableFilledModal}
                      handleDeleteVariable={deleteVariableFilled}
                      fromQuickSend
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "white",
                    borderTop: `1px solid ${grey[200]}`,
                    py: 0.5,
                    px: 0,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <IconButton
                      sx={{
                        color: "black",
                        fontSize: 13,
                        width: 20,
                        height: 20,
                        ...(showToolbar
                          ? {
                              fontWeight: "bold",
                              color: "secondary.main",
                              backgroundColor: grey[200],
                              borderRadius: 1,
                            }
                          : {}),
                      }}
                      onClick={() => setShowToolbar((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <Box component="i" className="fa-thin fa-font" />
                    </IconButton>
                    <IconButton
                      sx={{
                        color: "black",
                        fontSize: 13,
                        width: 20,
                        height: 20,
                        ...(editor.isActive("link")
                          ? {
                              fontWeight: "bold",
                              color: "secondary.main",
                              backgroundColor: grey[200],
                              borderRadius: 1,
                            }
                          : {}),
                      }}
                      onClick={openModal}
                    >
                      <Box component="i" className="fa-solid fa-link" />
                    </IconButton>
                    <IconButton
                      sx={{
                        color: "black",
                        fontSize: 13,
                        width: 20,
                        height: 20,
                      }}
                      onClick={() => setAttachmentDialogOpen(true)}
                    >
                      <Box component="i" className="fa-solid fa-paperclip" />
                    </IconButton>
                    <IconButton
                      sx={{
                        color: "black",
                        fontSize: 13,
                        width: 20,
                        height: 20,
                      }}
                      onClick={() => setSelectImageOpen(true)}
                    >
                      <Box component="i" className="fa-solid fa-image" />
                    </IconButton>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      ml: "auto",
                    }}
                  >
                    <Button
                      onClick={handleClose}
                      sx={{
                        textTransform: "none",
                        color: "#014339",
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={!hasChanges || saving || hasTemplatingIssues}
                      sx={{
                        textTransform: "none",
                        backgroundColor: "#014339",
                        color: "white",
                        fontSize: 13,
                        fontWeight: 500,
                        px: 2,
                        borderRadius: "8px",
                        "&:hover": { backgroundColor: "#012e27" },
                        "&:disabled": {
                          backgroundColor: "rgba(1,67,57,0.3)",
                          color: "white",
                        },
                      }}
                    >
                      {saving ? (
                        <CircularProgress size={16} sx={{ color: "white" }} />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </Box>
                </Box>

                <LinkModal
                  url={url}
                  closeModal={closeModal}
                  open={modalIsOpen}
                  onChangeUrl={(newUrl: Url) => setUrl(newUrl)}
                  onSaveLink={saveLink}
                  onRemoveLink={removeLink}
                />
                <VariableModal
                  variable={variable}
                  open={variableModalIsOpen}
                  closeModal={closeVariableModal}
                  onSave={handleSaveVariable}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Attachment file picker */}
      <AttachmentManager
        enteredSelection={handleAttachmentSelect}
        open={attachmentDialogOpen}
        setOpen={setAttachmentDialogOpen}
      />

      {/* Image picker for inline images */}
      <AttachmentManager
        enteredSelection={selectedImage}
        open={selectImageOpen}
        setOpen={setSelectImageOpen}
        imageOnly
      />

      <ConfirmDialog
        header="Unsaved changes"
        subheader="You have unsaved changes. Are you sure you want to leave?"
        open={confirmCloseOpen}
        handleClose={() => setConfirmCloseOpen(false)}
        handleConfirm={() => {
          setConfirmCloseOpen(false);
          onClose();
        }}
      />

      <ConfirmDialog
        header="Change contact?"
        subheader="Changing the contact will re-personalize all emails. Any unsaved changes or customizations you've made will be lost."
        open={confirmContactOpen}
        handleClose={() => {
          setConfirmContactOpen(false);
          setPendingContactId(null);
        }}
        handleConfirm={() => {
          setConfirmContactOpen(false);
          if (pendingContactId !== null) {
            handleContactChange(pendingContactId);
            setPendingContactId(null);
          }
        }}
        confirmText="Change"
        cancelText="Cancel"
      />
    </Dialog>
  );
}
